import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { LICENSE_AGREEMENT, PRIVACY_POLICY, USER_AGREEMENT } from '../mock';
import LegalModal from '../components/LegalModal';

const AuthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetData, setResetData] = useState({
    email: '',
    reset_code: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showUserAgreementModal, setShowUserAgreementModal] = useState(false);
  
  const [agreements, setAgreements] = useState({
    privacy: false,
    license: false,
    userAgreement: false
  });
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    password: '',
    confirmPassword: ''
  });

  const allAgreementsChecked = agreements.privacy && agreements.license && agreements.userAgreement;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast({ title: 'Ошибка', description: 'Заполните все поля', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.login(loginData);
      localStorage.setItem('token', response.token);
      localStorage.setItem('isAuthenticated', 'true');
      toast({ title: 'Успешный вход', description: 'Добро пожаловать в систему' });
      navigate('/dashboard');
    } catch (error) {
      toast({ title: 'Ошибка входа', description: error.response?.data?.detail || 'Неверный email или пароль', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.name || !registerData.email || !registerData.password) {
      toast({ title: 'Ошибка', description: 'Заполните обязательные поля', variant: 'destructive' });
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      toast({ title: 'Ошибка', description: 'Пароли не совпадают', variant: 'destructive' });
      return;
    }
    if (!allAgreementsChecked) {
      toast({ title: 'Ошибка', description: 'Необходимо принять все соглашения', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const userData = {
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone || null,
        date_of_birth: registerData.date_of_birth || null,
        password: registerData.password
      };
      const response = await authAPI.register(userData);
      localStorage.setItem('token', response.token);
      localStorage.setItem('isAuthenticated', 'true');
      toast({ title: 'Регистрация успешна', description: 'Ваш аккаунт создан' });
      navigate('/dashboard');
    } catch (error) {
      toast({ title: 'Ошибка регистрации', description: error.response?.data?.detail || 'Произошла ошибка при регистрации', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    if (!resetData.email) {
      toast({ title: 'Ошибка', description: 'Введите email', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.requestPasswordReset({ email: resetData.email });
      toast({ title: 'Код отправлен', description: response.message || 'Проверьте ваш email' });
      setResetStep(2);
    } catch (error) {
      toast({ title: 'Ошибка', description: error.response?.data?.detail || 'Произошла ошибка', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetData.reset_code || !resetData.new_password || !resetData.confirm_password) {
      toast({ title: 'Ошибка', description: 'Заполните все поля', variant: 'destructive' });
      return;
    }
    if (resetData.new_password !== resetData.confirm_password) {
      toast({ title: 'Ошибка', description: 'Пароли не совпадают', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword({
        email: resetData.email,
        reset_code: resetData.reset_code,
        new_password: resetData.new_password
      });
      toast({ title: 'Успешно', description: 'Пароль успешно изменен' });
      setShowPasswordReset(false);
      setResetStep(1);
      setResetData({ email: '', reset_code: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({ title: 'Ошибка', description: error.response?.data?.detail || 'Произошла ошибка', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Apple Liquid Glass 2026 input — matches dashboard style
  const inputClassName = "auth-input-glass text-white placeholder:text-white/35 focus:border-cyan-400/55 focus:bg-white/[0.04] rounded-full h-12 px-6 transition-all duration-200";

  return (
    <div className="relative w-full min-h-screen overflow-hidden bg-black auth-page-enter">
      {/* Чистый чёрный фон — после "всасывания" пользователь оказывается в пустоте,
          из которой плавно появляется форма авторизации */}

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-8 sm:py-12">
        <div className="w-full max-w-md auth-zoom-in">
          <button
            onClick={() => navigate('/')}
            className="fixed top-4 right-4 sm:top-6 sm:right-6 p-2.5 rounded-full glass-btn text-white/70 hover:text-white transition-all z-50"
            data-testid="close-auth-btn"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-semibold text-white mb-2 tracking-tight">AIMedGuru</h1>
            <p className="text-white/60 text-sm">Ваш персональный медицинский помощник</p>
          </div>

          {/* Apple Liquid Glass 2026 Card */}
          <div className="liquid-glass-card rounded-[40px] p-6 sm:p-8" data-testid="auth-glass-card">

            {showPasswordReset ? (
              /* PASSWORD RESET FORM */
              <div className="relative" data-testid="password-reset-form">
                <div className="flex items-center mb-6">
                  <button
                    type="button"
                    onClick={() => { setShowPasswordReset(false); setResetStep(1); setResetData({ email: '', reset_code: '', new_password: '', confirm_password: '' }); }}
                    className="p-2 -ml-2 rounded-full hover:bg-white/[0.1] transition-colors"
                    data-testid="back-from-reset-btn"
                  >
                    <ArrowLeft className="w-5 h-5 text-white/70" />
                  </button>
                  <h2 className="text-lg font-medium text-white text-center flex-1 pr-7">Восстановление пароля</h2>
                </div>

                {resetStep === 1 ? (
                  <form onSubmit={handleRequestPasswordReset} className="space-y-4">
                    <p className="text-white/60 text-sm mb-4">Введите ваш email, и мы отправим вам код для восстановления пароля</p>
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm font-medium">Email</Label>
                      <Input type="email" placeholder="your@email.com" value={resetData.email}
                        onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                        className={inputClassName} disabled={loading} data-testid="reset-email" />
                    </div>
                    <Button type="submit" disabled={loading}
                      className="w-full glass-btn-accent text-white rounded-full mt-6 h-12 font-medium"
                      data-testid="request-reset-btn"
                    >
                      {loading ? 'Отправка...' : 'Отправить код'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <p className="text-white/50 text-sm mb-4">Код отправлен на {resetData.email}. Введите код и новый пароль</p>
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm font-medium">Код восстановления</Label>
                      <Input type="text" placeholder="000000" value={resetData.reset_code}
                        onChange={(e) => setResetData({ ...resetData, reset_code: e.target.value })}
                        className={inputClassName} disabled={loading} maxLength={6} data-testid="reset-code" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm font-medium">Новый пароль</Label>
                      <div className="relative">
                        <Input type={showPassword ? "text" : "password"} placeholder="••••••••" value={resetData.new_password}
                          onChange={(e) => setResetData({ ...resetData, new_password: e.target.value })}
                          className={`${inputClassName} pr-12`} disabled={loading} data-testid="reset-new-password" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/70 text-sm font-medium">Подтвердите новый пароль</Label>
                      <div className="relative">
                        <Input type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" value={resetData.confirm_password}
                          onChange={(e) => setResetData({ ...resetData, confirm_password: e.target.value })}
                          className={`${inputClassName} pr-12`} disabled={loading} data-testid="reset-confirm-password" />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors">
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" disabled={loading}
                      className="w-full glass-btn-accent text-white rounded-full mt-6 h-12 font-medium"
                      data-testid="reset-password-btn"
                    >
                      {loading ? 'Сохранение...' : 'Изменить пароль'}
                    </Button>
                  </form>
                )}
              </div>
            ) : !showRegister ? (
              /* LOGIN FORM */
              <div className="relative" data-testid="login-form">
                <h2 className="text-lg font-medium text-white text-center mb-6">Вход в аккаунт</h2>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white/70 text-sm font-medium">Email</Label>
                    <Input type="email" placeholder="your@email.com" value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className={inputClassName} disabled={loading} data-testid="login-email" />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/70 text-sm font-medium">Пароль</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} placeholder="••••••••" value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className={`${inputClassName} pr-12`} disabled={loading} data-testid="login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading}
                    className="w-full glass-btn-accent text-white rounded-full mt-6 h-12 font-medium"
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Загрузка...' : 'Войти'}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <button type="button" onClick={() => setShowPasswordReset(true)}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                    data-testid="forgot-password-btn"
                  >
                    Забыли пароль?
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-white/[0.1]">
                  <p className="text-white/60 text-sm text-center mb-4">Нет аккаунта?</p>
                  <Button type="button" onClick={() => setShowRegister(true)}
                    className="w-full glass-btn text-white rounded-full h-12 font-medium"
                    data-testid="go-to-register-btn"
                  >
                    Зарегистрироваться
                  </Button>
                </div>
              </div>
            ) : (
              /* REGISTER FORM */
              <div className="relative" data-testid="register-form">
                <div className="flex items-center mb-6">
                  <button type="button" onClick={() => setShowRegister(false)}
                    className="p-2 -ml-2 rounded-full hover:bg-white/[0.1] transition-colors"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-5 h-5 text-white/70" />
                  </button>
                  <h2 className="text-lg font-medium text-white text-center flex-1 pr-7">Регистрация</h2>
                </div>
                
                <form onSubmit={handleRegister} className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-white/70 text-sm font-medium">Имя *</Label>
                    <Input type="text" placeholder="Иван Петров" value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      className={inputClassName} disabled={loading} data-testid="register-name" />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/70 text-sm font-medium">Email *</Label>
                    <Input type="email" placeholder="your@email.com" value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className={inputClassName} disabled={loading} data-testid="register-email" />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/70 text-sm font-medium">Пароль *</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} placeholder="••••••••" value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className={`${inputClassName} pr-12`} disabled={loading} data-testid="register-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/70 text-sm font-medium">Подтвердите пароль *</Label>
                    <div className="relative">
                      <Input type={showConfirmPassword ? "text" : "password"} placeholder="••••••••" value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        className={`${inputClassName} pr-12`} disabled={loading} data-testid="register-confirm-password" />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors">
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Agreement Checkboxes */}
                  <div className="space-y-3 pt-4 border-t border-white/[0.08] mt-4">
                    <p className="text-white/50 text-xs mb-2">Продолжая регистрацию, вы подтверждаете, что ознакомились и согласны с:</p>
                    
                    <div className="flex items-start space-x-3">
                      <Checkbox id="privacy" checked={agreements.privacy}
                        onCheckedChange={(checked) => setAgreements({ ...agreements, privacy: checked })}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                        data-testid="privacy-checkbox" />
                      <label htmlFor="privacy" className="text-sm text-white/60 leading-tight">
                        <button type="button" onClick={() => setShowPrivacyModal(true)}
                          className="text-cyan-400 hover:text-cyan-300 underline" data-testid="privacy-link">
                          Политикой конфиденциальности
                        </button>
                      </label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox id="license" checked={agreements.license}
                        onCheckedChange={(checked) => setAgreements({ ...agreements, license: checked })}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                        data-testid="license-checkbox" />
                      <label htmlFor="license" className="text-sm text-white/60 leading-tight">
                        <button type="button" onClick={() => setShowLicenseModal(true)}
                          className="text-cyan-400 hover:text-cyan-300 underline" data-testid="license-link">
                          Лицензионным соглашением
                        </button>
                      </label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Checkbox id="userAgreement" checked={agreements.userAgreement}
                        onCheckedChange={(checked) => setAgreements({ ...agreements, userAgreement: checked })}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                        data-testid="user-agreement-checkbox" />
                      <label htmlFor="userAgreement" className="text-sm text-white/60 leading-tight">
                        <button type="button" onClick={() => setShowUserAgreementModal(true)}
                          className="text-cyan-400 hover:text-cyan-300 underline" data-testid="user-agreement-link">
                          Пользовательским соглашением
                        </button>
                      </label>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading || !allAgreementsChecked}
                    className={`w-full text-white rounded-full mt-6 h-12 font-medium transition-all ${
                      allAgreementsChecked 
                        ? 'glass-btn-accent' 
                        : 'glass-btn opacity-50 cursor-not-allowed'
                    }`}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Загрузка...' : 'Создать аккаунт'}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legal Modals */}
      <LegalModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} document={PRIVACY_POLICY} title="Политика конфиденциальности" />
      <LegalModal isOpen={showLicenseModal} onClose={() => setShowLicenseModal(false)} document={LICENSE_AGREEMENT} title="Лицензионное соглашение" />
      <LegalModal isOpen={showUserAgreementModal} onClose={() => setShowUserAgreementModal(false)} document={USER_AGREEMENT} title="Пользовательское соглашение" />
    </div>
  );
};

export default AuthPage;
